import type { MouseEvent as ReactMouseEvent } from 'react';
import type { ConsentSelections } from 'src/transcend/types';
import type { TrackingConsentDetails } from '@transcend-io/airgap.js-types';
import { z } from 'zod';

export const LanguageRegionCodeSchema = z.enum(['en-US', 'fr-CA', 'en-CA']);
// Type alias to ensure it matches the Zod schema for runtime type checking
export type LanguageRegionCode = z.infer<typeof LanguageRegionCodeSchema>;

export type AppState = {
    openDrawer: boolean;
    acceptAll: boolean;
    choicesConfirmedAtleastOnce: boolean;
    confirmMyChoices: boolean;
    event: ReactMouseEvent<HTMLButtonElement> | null;
    eventProcessed: boolean;
    consentSelections: ConsentSelections;
    optInPersonalizedAdvertising: boolean;
    optInAnalytics: boolean;
    optInFunctional: boolean;
    unsavedChanges: boolean;
    isErred: boolean;
    errorMessage: string | null;
    trackingConsentDetails: TrackingConsentDetails | null;
    showConsentUI: boolean;
    selectedLanguage: LanguageRegionCode;
    bannerClosedOnError: boolean;
};

export enum AppActionTypes {
    OPEN_DRAWER = 'openDrawer',
    CLOSE_DRAWER = 'closeDrawer',
    CLOSE_BANNER_ON_ERROR = 'closeBannerOnError',
    RESET_CLOSE_BANNER_ON_ERROR = 'resetCloseBannerOnError',
    ACCEPT_ALL = 'acceptAll',
    ACCEPT_ALL_COMPLETED = 'acceptAllCompleted',
    ACCEPT_ALL_FAILED = 'acceptAllFailed',
    CONFIRM_MY_CHOICES = 'confirmMyChoices',
    CONFIRM_MY_CHOICES_COMPLETED = 'confirmMyChoicesCompleted',
    CONFIRM_MY_CHOICES_FAILED = 'confirmMyChoicesFailed',
    SET_CONSENT_SELECTIONS = 'setConsentSelections',
    SET_OPT_IN_PERSONALIZED_ADVERTISING = 'setOptInPersonalizedAdvertising',
    SET_OPT_IN_PERSONALIZED_ANALYTICS = 'setOptInPersonalizedAnalytics',
    SET_OPT_IN_PERSONALIZED_FUNCTIONAL = 'setOptInPersonalizedAFunctional',
    SET_CHOICES_CONFIRMED_ATLEAST_ONCE = 'setChoicesConfirmedAtleastOnce',
    SET_TRACKING_CONSENT_DETAILS = 'setTrackingConsentDetails',
    SET_TRACKING_CONSENT_DETAILS_FAILED = 'setTrackingConsentDetailsFailed',
    SET_UNSAVED_CHANGES = 'setUnsavedChanges',
    UNSAVED_CHANGES = 'unsavedChanges',
    UNSAVED_CHANGES_REVERTED = 'unsavedChangesReverted',
}

export type AppAction = {
    type:
        | AppActionTypes.ACCEPT_ALL
        | AppActionTypes.ACCEPT_ALL_COMPLETED
        | AppActionTypes.ACCEPT_ALL_FAILED
        | AppActionTypes.CONFIRM_MY_CHOICES
        | AppActionTypes.CONFIRM_MY_CHOICES_COMPLETED
        | AppActionTypes.CONFIRM_MY_CHOICES_FAILED
        | AppActionTypes.OPEN_DRAWER
        | AppActionTypes.CLOSE_DRAWER
        | AppActionTypes.CLOSE_BANNER_ON_ERROR
        | AppActionTypes.RESET_CLOSE_BANNER_ON_ERROR
        | AppActionTypes.SET_CONSENT_SELECTIONS
        | AppActionTypes.SET_OPT_IN_PERSONALIZED_ADVERTISING
        | AppActionTypes.SET_OPT_IN_PERSONALIZED_ANALYTICS
        | AppActionTypes.SET_OPT_IN_PERSONALIZED_FUNCTIONAL
        | AppActionTypes.SET_CHOICES_CONFIRMED_ATLEAST_ONCE
        | AppActionTypes.SET_TRACKING_CONSENT_DETAILS
        | AppActionTypes.SET_TRACKING_CONSENT_DETAILS_FAILED
        | AppActionTypes.SET_UNSAVED_CHANGES
        | AppActionTypes.UNSAVED_CHANGES
        | AppActionTypes.UNSAVED_CHANGES_REVERTED;
    consentSelections?: ConsentSelections;
    payload?: boolean;
    mouseEvent?: ReactMouseEvent<HTMLButtonElement> | null;
    isErred?: boolean;
    errorMessage?: string;
    trackingConsentDetails?: TrackingConsentDetails;
    unsavedChanges?: boolean;
};

export enum AirgapPurposes {
    Analytics = 'Analytics',
    Functional = 'Functional',
    SaleOfInfo = 'SaleOfInfo',
}
